import {
  createTransaction,
  getCredentials,
  getOrg,
  getProfile,
  getTxnInfo
} from '@api/form-verification';
import { startRefreshAccessTokenAtRegularInterval } from '@auth/api/auth';
import DisclaimerDialog from '@components/disclaimer-dialog';
import { DEFAULT_ORG } from '@global/constants/general';
import {
  growthBookProfileVerificationsFeature,
  ProfileVerifications,
  TxnState
} from '@global/constants/verification';
import { safeLocalStorageSetItem } from '@global/helpers/general';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { Box, CircularProgress } from '@mui/material';
import { clearFormData } from '@redux/slices/form-data';
import { updateMiscStore } from '@redux/slices/misc';
import { RootState } from '@redux/store';
import { growthbook } from '@services/growthbook';
import { navigateTo } from '@services/navigation';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BeginFormVerification from './begin-verification';
import Form from './form';
import FormContext from './form/context';
import { handleFetchConfigs } from './helpers';

function FormVerification() {
  const verificationsForProfile = useFeatureValue(
    growthBookProfileVerificationsFeature,
    ProfileVerifications.PASSPORT
  );
  const navigate = useNavigate();
  const auth: any = useSelector((s: RootState) => s.auth);
  const { accessToken } = auth;
  const location = useLocation();
  const dispatch = useDispatch();
  const query = new URLSearchParams(location.search);
  const { formId = '', txnId: txnIdFromQuery = null } = useParams();
  const orgId = query.get('orgId') ?? '';
  const sso = query.get('sso') ?? '';
  const formData = useSelector((s: RootState) => s.formData);
  const { orgInfo, showInitScreen, txnId: txnIdFromFormData, txnInfo } = formData ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const { disclaimerAgreed } = useSelector((s: RootState) => s.misc);
  // const profileFromRedux: any = useSelector((s: RootState) => s.profile);
  // const profileVerified = profileFromRedux?.profileDetails?.name ? true : false;
  const txnId = txnIdFromQuery || txnIdFromFormData;
  const showDisclaimer =
    import.meta.env.VITE_SHOW_DISCLAIMER &&
    !disclaimerAgreed?.[txnInfo?._id] &&
    txnInfo?.state === TxnState.NOT_INITIALIZED;

  const handleAgreeDisclaimer = () => {
    dispatch(
      updateMiscStore({
        disclaimerAgreed: { ...(disclaimerAgreed ?? {}), [txnInfo?._id]: true }
      })
    );
  };

  const [profile, setProfile] = useState({});
  const notAllowed = () => {
    const partnerOrgId = import.meta.env.VITE_PARTNER_ORG_ID;
    if (import.meta.env.VITE_NSDC_PARTNER) return false;
    if (partnerOrgId && partnerOrgId !== DEFAULT_ORG && orgId !== partnerOrgId) {
      return true;
    }
    return false;
  };
  const orgIdBasedAccessAllowed = !notAllowed();

  const fetchOrgDetails = async () => {
    clearFormData({
      formId
    });
    setIsLoading(true);
    if (orgId) {
      const res = await getOrg(orgId);
      setIsLoading(false);
      if (res?.code === 0) {
        const data = res?.data;
        dispatch(
          clearFormData({
            orgInfo: {
              ...(orgInfo ?? {}),
              name: data?.displayName,
              imageUrl: data?.imageUrl,
              orgId,
              additionalDetail: data?.additionalDetail
            },
            formId
          })
        );
        if (orgIdBasedAccessAllowed && accessToken) {
          let txnId = txnIdFromQuery;
          if (txnIdFromQuery) {
            handleFetchTxnInfo(txnIdFromQuery);
          } else {
            txnId = await createTxn();
          }
          dispatch(
            updateMiscStore({
              searchedQuery: {
                orgId: orgId,
                txnId: txnId,
                link: window.location.href,
                formId
              }
            })
          );
        }
      }
    }
  };

  const handleFetchTxnInfo = async (txnId: string) => {
    const res: any = await getTxnInfo(txnId, orgId);
    dispatch(
      updateMiscStore({
        searchedQuery: {
          orgId: orgId,
          txnId: txnId,
          link: window.location.href,
          formId
        }
      })
    );
    if (res?.code === 0) {
      safeLocalStorageSetItem(
        'reqdIdsForForm',
        JSON.stringify({ orgId, formId, txnIdFromQuery: txnId })
      );
      const credRes = await getCredentials(res?.data);
      if (credRes?.code === 0) {
        handleFetchConfigs(res?.data, verificationsForProfile);
      } else {
        // navigate('/');
      }
    } else {
      navigateTo('/error', {
        state: {
          message: 'Failed to fetch transaction info'
        }
      });
      // navigate('/');
    }
    setIsLoading(false);
  };

  const createTxn = async () => {
    const res = await createTransaction({ orgId, eKycFormId: formId });
    if (res?.code === 0) {
      const txnId = res?.data?.transactionId;
      handleFetchTxnInfo(txnId);
      return txnId;
    }
  };

  const getProfileDetails = async () => {
    const res = await getProfile();
    if (res?.code === 0) {
      setProfile(res?.data);
    }
    setIsLoading(false);
  };

  const handleSSOLogin = async () => {
    try {
      // Extract URL parameters
      const txnId = txnIdFromQuery;

      // Store the refresh token (SSO parameter) in localStorage
      safeLocalStorageSetItem('refreshToken', sso);

      // Store other parameters for later use
      if (txnId) safeLocalStorageSetItem('txnId', txnId);
      if (formId) safeLocalStorageSetItem('formId', formId);
      if (orgId) safeLocalStorageSetItem('orgId', orgId);

      // Start the refresh token process to generate access token
      const accessToken = await startRefreshAccessTokenAtRegularInterval(sso);

      await new Promise((resolve) => setTimeout(resolve, 200));
      if (accessToken) {
        return navigate('/select-evaluation-partner', { state: { redirectIfVerified: true } });
      }
    } catch (error) {
      console.error('SSO Login Error:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fn = () => {
      setIsLoading(true);
      sessionStorage.setItem('reqdIdsForForm', JSON.stringify({ orgId, formId, txnIdFromQuery }));
      if (sso) {
        return handleSSOLogin().finally(() => {
          setIsLoading(false);
        });
      }
      if (!accessToken) {
        fetchOrgDetails();
      } else {
        getProfileDetails();
        if (
          txnId &&
          orgInfo?.orgId === orgId &&
          formData?.formId === formId &&
          orgIdBasedAccessAllowed
        ) {
          handleFetchTxnInfo(txnId);
        } else {
          fetchOrgDetails();
        }
      }
    };
    fn();
    growthbook.loadFeatures();
  }, []);

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : !showInitScreen ? (
        <FormContext.Provider value={{ profile }}>
          <DisclaimerDialog open={showDisclaimer} onAgree={handleAgreeDisclaimer} />
          <Form />
        </FormContext.Provider>
      ) : (
        <BeginFormVerification
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          orgIdBasedAccessAllowed={orgIdBasedAccessAllowed}
        />
      )}
    </>
  );
}

export default FormVerification;
